import type { ReactNode } from 'react';
import {
  AppShell,
  Burger,
  MantineProvider,
  ScrollArea,
  type AppShellProps,
  type ScrollAreaProps,
} from '@mantine/core';
import { ToastHost } from '../feedback/ToastHost';
import { appTheme } from '../theme/appTheme';
import shellStyles from '../layout/AppShellLayout.module.css';

export function UiProvider({ children }: { children: ReactNode }) {
  return (
    <MantineProvider theme={appTheme} defaultColorScheme="light">
      <ToastHost />
      {children}
    </MantineProvider>
  );
}

export function AppShellLayout({ children, className, ...props }: AppShellProps) {
  return (
    <AppShell
      layout="alt"
      className={className}
      styles={{
        root: { height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' },
        main: {
          // Concrete sizes come from AppShellLayout.module.css (.main) — keep inline
          // overrides minimal so CSS module !important rules win consistently.
          minHeight: 0,
        },
      }}
      {...props}
    >
      {children}
    </AppShell>
  );
}

export function AppShellHeader({ children }: { children: ReactNode }) {
  return <AppShell.Header className={shellStyles.header}>{children}</AppShell.Header>;
}

export function AppShellNavbar({ children }: { children: ReactNode }) {
  return <AppShell.Navbar p={0}>{children}</AppShell.Navbar>;
}

export function AppShellMain({ children }: { children: ReactNode }) {
  return (
    <AppShell.Main className={shellStyles.main}>
      {/* mainInner is the page scrollport (overflow-y: auto). Workspace pages nest their own scroll. */}
      <div className={shellStyles.mainInner}>{children}</div>
    </AppShell.Main>
  );
}

export function BurgerButton({
  opened,
  onClick,
  label = 'Открыть навигацию',
}: {
  opened: boolean;
  onClick: () => void;
  label?: string;
}) {
  return <Burger opened={opened} onClick={onClick} aria-label={label} size="sm" />;
}

export function AppScrollArea({ children, ...props }: ScrollAreaProps) {
  return (
    <ScrollArea type="auto" scrollbars="y" {...props}>
      {children}
    </ScrollArea>
  );
}
